import { Check, Play, FileText, Plus, Lock, Pencil, Trash2, ChevronUp, ChevronDown, Flag } from "lucide-react";

export const fmtDate = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "");

const PLAN_STATUS_META = {
  a_venir: { label: "À venir", cls: "bg-[var(--parchment)] text-[var(--ink-soft)]" },
  en_cours: { label: "En cours", cls: "bg-[var(--maroon)]/10 text-[var(--maroon)]" },
  termine: { label: "Terminé", cls: "bg-[var(--ok)]/10 text-[var(--ok)]" },
  en_retard: { label: "En retard", cls: "bg-[var(--late)]/10 text-[var(--late)]" },
};

export const BAR_COLOR_META = {
  vert: { fill: "bg-[var(--ok)]", text: "text-[var(--ok)]", label: "Dans les temps" },
  jaune: { fill: "bg-[var(--just)]", text: "text-[var(--just)]", label: "En retard" },
  rouge: { fill: "bg-[var(--late)]", text: "text-[var(--late)]", label: "Retard important" },
};

export function barStatusLabel(bar) {
  if (!bar) return "";
  if (bar.color === "rouge") return `RETARD IMPORTANT – ${bar.delay} jours`;
  if (bar.color === "jaune") return `Chapitre en retard de ${bar.delay} jour${bar.delay > 1 ? "s" : ""}`;
  if (bar.days_remaining !== null && bar.days_remaining !== undefined) {
    return `Dans les temps · ${bar.days_remaining} jour${bar.days_remaining > 1 ? "s" : ""} restant${bar.days_remaining > 1 ? "s" : ""}`;
  }
  return "Dans les temps";
}

export function ChapterLiveBar({ chapter, compact = false }) {
  const bar = chapter.bar;
  if (!bar) return null;
  const meta = BAR_COLOR_META[bar.color] || BAR_COLOR_META.vert;
  return (
    <div className={compact ? "" : "mt-4"} data-testid={`chapter-live-bar-${chapter.order}`}>
      <div className="bar-track">
        <div className={`bar-fill ${meta.fill}`} style={{ width: `${bar.percent ?? 100}%` }} />
      </div>
      <div className={`mt-1.5 flex items-center justify-between text-xs font-medium ${meta.text}`}>
        <span data-testid={`chapter-bar-status-${chapter.order}`} className={bar.color === "rouge" ? "font-bold" : ""}>
          {barStatusLabel(bar)}
        </span>
        {bar.percent !== null && bar.percent !== undefined && (
          <span className="font-mono-spec">{bar.percent} %</span>
        )}
      </div>
    </div>
  );
}

export function FinalDelayBadge({ chapter }) {
  if (!chapter.ended_at) return null;
  const d = chapter.delay_days;
  if (d === null || d === undefined || d === 0) {
    return <span className="text-xs text-[var(--ok)] inline-flex items-center gap-1"><Flag size={11} /> Terminé dans les temps</span>;
  }
  if (d > 0) {
    return <span className="text-xs text-[var(--late)] inline-flex items-center gap-1"><Flag size={11} /> Terminé avec {d} jour{d > 1 ? "s" : ""} de retard</span>;
  }
  return <span className="text-xs text-[var(--ok)] inline-flex items-center gap-1"><Flag size={11} /> Terminé avec {-d} jour{-d > 1 ? "s" : ""} d'avance</span>;
}

function dotColorFor(chapter, isCurrent, isLocked) {
  if (chapter.ended_at) return "bg-[var(--ok)]";
  if (isCurrent) return "bg-[var(--maroon)]";
  if (isLocked) return "bg-[var(--parchment-2)]";
  return "bg-[var(--muted)]";
}

function Step({ active, label }) {
  return (
    <span className={`inline-flex items-center gap-1 ${active ? "text-[var(--ok)]" : "text-[var(--muted)]"}`}>
      <span className={`w-2 h-2 ${active ? "bg-[var(--ok)]" : "bg-[var(--parchment-2)]"}`} />
      {label}
    </span>
  );
}

function Sep() {
  return <span className="text-[var(--parchment-2)]">—</span>;
}

function planPeriodLabel(chapter) {
  if (!chapter.start_date && !chapter.end_date) return "Dates prévisionnelles non définies";
  const from = chapter.start_date ? `du ${fmtDate(chapter.start_date)}` : "";
  const to = chapter.end_date ? ` au ${fmtDate(chapter.end_date)}` : "";
  return `Prévu ${from}${to}`;
}

function EvalChips({ evaluations }) {
  if (!evaluations || evaluations.length === 0) return null;
  return (
    <div className="mt-3 text-xs text-[var(--ink-soft)] flex flex-wrap gap-x-3 gap-y-1">
      {evaluations.map((ev) => (
        <span key={ev.id} className="inline-flex items-center gap-1">
          <FileText size={11} /> {ev.title}{ev.date ? ` · ${fmtDate(ev.date)}` : ""}
        </span>
      ))}
    </div>
  );
}

function ChapterInfo({ chapter }) {
  const plan = PLAN_STATUS_META[chapter.plan_status] || PLAN_STATUS_META.a_venir;
  const evalCount = chapter.evaluations?.length || 0;
  return (
    <div>
      <h3 className="font-serif-display text-xl font-semibold">{chapter.title}</h3>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        <span className="eyebrow !text-[var(--ink-soft)]">{planPeriodLabel(chapter)}</span>
        <span className={`px-2 py-0.5 ${plan.cls}`} data-testid={`plan-status-badge-${chapter.order}`}>{plan.label}</span>
        <FinalDelayBadge chapter={chapter} />
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs">
        <Step active={!!chapter.started_at} label={chapter.started_at ? `Démarré · ${fmtDate(chapter.started_at)}` : "Démarré"} />
        <Sep />
        <Step active={evalCount > 0} label={`Évaluation${evalCount > 1 ? "s" : ""}${evalCount ? ` (${evalCount})` : ""}`} />
        <Sep />
        <Step active={!!chapter.ended_at} label={chapter.ended_at ? `Terminé · ${fmtDate(chapter.ended_at)}` : "Terminé"} />
      </div>

      <ChapterLiveBar chapter={chapter} />
      <EvalChips evaluations={chapter.evaluations} />
    </div>
  );
}

function StartAction({ chapter, isLocked, busy, onStart }) {
  return (
    <div className="flex flex-col items-end gap-1.5">
      <button
        onClick={onStart}
        disabled={busy || isLocked}
        className={`btn-primary !py-2 !px-4 text-sm ${isLocked ? "opacity-40 cursor-not-allowed" : ""}`}
        data-testid={`start-btn-${chapter.order}`}
      >
        <Play size={14} /> Démarrer le chapitre
      </button>
      {isLocked && (
        <span className="text-[11px] text-[var(--muted)] max-w-[220px] text-right inline-flex items-start gap-1" data-testid={`start-locked-msg-${chapter.order}`}>
          <Lock size={11} className="shrink-0 mt-0.5" />
          Vous devez terminer le chapitre en cours avant de pouvoir démarrer celui-ci.
        </span>
      )}
    </div>
  );
}

function ActiveActions({ chapter, busy, onAddEval, onEnd }) {
  const hasEval = !!chapter.evaluations?.length;
  return (
    <>
      <button onClick={onAddEval} disabled={busy} className="btn-secondary !py-2 !px-4 text-sm" data-testid={`add-eval-btn-${chapter.order}`}>
        <Plus size={14} /> Évaluation
      </button>
      <div className="flex flex-col items-end gap-1.5">
        <button
          onClick={onEnd}
          disabled={busy || !hasEval}
          className={`btn-primary !py-2 !px-4 text-sm ${!hasEval ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid={`end-btn-${chapter.order}`}
        >
          <Check size={14} /> Fin de chapitre
        </button>
        {!hasEval && (
          <span className="text-[11px] text-[var(--muted)] max-w-[220px] text-right">
            Ajoutez au moins une évaluation (nommée et datée) avant de clôturer.
          </span>
        )}
      </div>
    </>
  );
}

function DirectionDatesButton({ chapter, isDirection, onEditDates }) {
  const hasActivity = chapter.started_at || chapter.evaluations?.length > 0 || chapter.ended_at;
  if (!isDirection || !hasActivity) return null;
  return (
    <button
      onClick={onEditDates}
      className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
      title="Corriger les dates réelles (direction)"
      data-testid={`edit-dates-btn-${chapter.order}`}
    >
      <Pencil size={13} /> Corriger les dates
    </button>
  );
}

function WorkflowActions({ chapter, isLocked, canEdit, isDirection, busy, onStart, onEnd, onAddEval, onEditDates }) {
  return (
    <>
      {canEdit && !chapter.started_at && (
        <StartAction chapter={chapter} isLocked={isLocked} busy={busy} onStart={onStart} />
      )}
      {chapter.started_at && !chapter.ended_at && canEdit && (
        <ActiveActions chapter={chapter} busy={busy} onAddEval={onAddEval} onEnd={onEnd} />
      )}
      {chapter.ended_at && (
        <span className="text-xs text-[var(--ok)] inline-flex items-center gap-1"><Check size={14} /> Validé</span>
      )}
      <DirectionDatesButton chapter={chapter} isDirection={isDirection} onEditDates={onEditDates} />
    </>
  );
}

function MoveButtons({ chapter, busy, canMoveUp, canMoveDown, onMoveUp, onMoveDown }) {
  if (!canMoveUp && !canMoveDown) return null;
  return (
    <>
      <button
        onClick={onMoveUp}
        disabled={!canMoveUp || busy}
        className={`btn-secondary !py-1 !px-2 text-xs ${!canMoveUp ? "opacity-30 cursor-not-allowed" : ""}`}
        title="Monter dans le programme"
        data-testid={`move-up-chapter-btn-${chapter.order}`}
      >
        <ChevronUp size={13} />
      </button>
      <button
        onClick={onMoveDown}
        disabled={!canMoveDown || busy}
        className={`btn-secondary !py-1 !px-2 text-xs ${!canMoveDown ? "opacity-30 cursor-not-allowed" : ""}`}
        title="Descendre dans le programme"
        data-testid={`move-down-chapter-btn-${chapter.order}`}
      >
        <ChevronDown size={13} />
      </button>
    </>
  );
}

function DeletePlanButton({ chapter, confirmDelete, onAskDelete, onConfirmDelete }) {
  if (chapter.started_at) return null;
  if (confirmDelete) {
    return (
      <button
        onClick={onConfirmDelete}
        className="!py-1 !px-2 text-xs bg-[var(--late)] text-white"
        data-testid={`confirm-delete-chapter-plan-${chapter.order}`}
      >
        Confirmer ?
      </button>
    );
  }
  return (
    <button
      onClick={onAskDelete}
      className="btn-secondary !py-1 !px-2 text-xs !text-[var(--late)]"
      title="Retirer du programme"
      data-testid={`delete-chapter-plan-btn-${chapter.order}`}
    >
      <Trash2 size={13} />
    </button>
  );
}

function PlanActions({ chapter, busy, onEditPlan, confirmDelete, onAskDelete, onConfirmDelete, canMoveUp, canMoveDown, onMoveUp, onMoveDown }) {
  return (
    <div className="flex items-center gap-1.5 lg:justify-end">
      <MoveButtons chapter={chapter} busy={busy} canMoveUp={canMoveUp} canMoveDown={canMoveDown} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
      <button
        onClick={onEditPlan}
        className="btn-secondary !py-1 !px-2 text-xs"
        title="Modifier titre / dates prévisionnelles"
        data-testid={`edit-chapter-plan-btn-${chapter.order}`}
      >
        <Pencil size={13} />
      </button>
      <DeletePlanButton chapter={chapter} confirmDelete={confirmDelete} onAskDelete={onAskDelete} onConfirmDelete={onConfirmDelete} />
    </div>
  );
}

const BORDER_BY_BAR_COLOR = {
  rouge: "border-[var(--late)]",
  jaune: "border-[var(--just)]",
  vert: "border-[var(--maroon)]",
};

export default function ChapterRow(props) {
  const { chapter, isLocked, isCurrent, canEdit, planEditable, isDirection } = props;
  let borderCls = isCurrent ? "border-[var(--maroon)]" : "border-[var(--parchment-2)]";
  if (chapter.bar) {
    borderCls = BORDER_BY_BAR_COLOR[chapter.bar.color] || "border-[var(--maroon)]";
  }
  return (
    <div
      className={`bg-white border ${borderCls} p-6 grid lg:grid-cols-[auto_1fr_auto] gap-6 items-start`}
      data-testid={`chapter-row-${chapter.order}`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-3 h-3 ${dotColorFor(chapter, isCurrent, isLocked)}`} />
        <div className="font-mono-spec text-xs text-[var(--muted)]">CHAPITRE {chapter.order}</div>
      </div>

      <ChapterInfo chapter={chapter} />

      {(canEdit || isDirection) && (
        <div className="flex flex-col gap-2 lg:items-end">
          <WorkflowActions {...props} />
          {planEditable && <PlanActions {...props} />}
        </div>
      )}
    </div>
  );
}
